import type { BaseDevice } from '../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { keyboardPerfCfgFeature } from '../../../features/keyboard-performance/contract'
import type { KeyboardPerfCfg, SleepTime, USBReportRate } from '../../../types/keyboardPerCfg'
import type { CH315KeyboardProtocol } from '../protocols'
import { CH315_FEATURE_LAYOUT, getCH315ConfigBase } from '../types/feature-types'
import { CH315FeatureMemory } from './memory'

const REPORT_RATE_BY_PROTOCOL_INDEX: readonly USBReportRate[] = [
  0x08, 0x04, 0x02, 0x01, 0x84, 0x82, 0x81,
]

export class CH315KeyboardPerfCfg extends keyboardPerfCfgFeature {
  private readonly memory: CH315FeatureMemory

  constructor(device: BaseDevice, protocol: CH315KeyboardProtocol) {
    super(device)
    this.memory = new CH315FeatureMemory(protocol)
  }

  async getReportRate(): Promise<USBReportRate> {
    const data = await this.memory.read(CH315_FEATURE_LAYOUT.basicInfoReportRateAddress, 4)
    const rate = REPORT_RATE_BY_PROTOCOL_INDEX[data[0]]
    if (rate === undefined) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED_VERSION,
        `CH315 report-rate index ${data[0]} cannot be represented by USBReportRate`,
      )
    }
    return rate
  }

  async getSleepTime(): Promise<SleepTime> {
    const data = await this.readConfigPerf()
    return data[0]
  }

  async getAllPerfCfg(): Promise<KeyboardPerfCfg> {
    const [usbReportRate, perf] = await Promise.all([this.getReportRate(), this.readConfigPerf()])
    return { usbReportRate, sleepTime: perf[0] }
  }

  /** CH315 contract 尚未包含扫描类型；保留强类型具体实现供设备级调用。 */
  async getScanType(): Promise<0 | 1 | 2> {
    const value = (await this.readConfigPerf())[1]
    if (value > 2) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `Invalid CH315 scan type ${value}`)
    }
    return value as 0 | 1 | 2
  }

  // 配置块尾部 Checksum32 的算法/提交语义未确认，所有性能写操作继承 NOT_SUPPORTED。

  private readConfigPerf(): Promise<Uint8Array> {
    return this.memory.read(
      getCH315ConfigBase(this.device.currentConfig.index) + CH315_FEATURE_LAYOUT.configPerfOffset,
      4,
    )
  }
}
